<template>
  <section class="position-relative pt-4 select-items px-3" v-if="!isLoading && !initialLoading">
    <b-row class="justify-content-md-center">
      <b-col lg="10" class="pl-sm-3 pr-sm-3 pl-0 pr-0">
        <div v-if="orders.length">
          <template v-for="order in orders">
            <div class="orders-container" :key="'order-' + order.id.toString()">
              <div v-if="!isSubscriptionOnly || debug" class="card-header">
                <div class="top-container d-flex justify-content-between align-items-center">
                  <div class="d-flex align-items-center">
                    <span class="top-container-text order_number">#{{order.display_order_id || order.order_id}}</span>
                    <span class="top-container-text order_date">
                      <span class="separator">|</span>
                      {{getShortHumanReadableDate(order.created)}}
                    </span>
                    <span class="separator">|</span>
                    <span class="badge order-status payment_status active" :class="getPaymentStatusClass(order)">{{getPaymentStatus(order)}}</span>
                    <template v-if="debug">
                      <span class="separator"></span>
                      <span class="top-container-text">{{getGatewayString(order)}}</span>
                    </template>
                  </div>
                </div>
              </div>
              <div class="order-container">
                <div
                  :class="{'clickable-order-container': !useIntentButtons}"
                  v-for="item in order.items"
                  :key="'item-' + item.id.toString()"
                  @click="selectItemArea($event, item)"
                >
                  <OrderItemCard
                    :session="session"
                    :item="item"
                    :currency="order.currency"
                    :selected="isSelected(item)"
                    :show-buttons="true"
                    :noselect="noselect"
                    :item-select-label="itemSelectLabel"
                    :use-intents="useIntentButtons"
                    :is-subscription-view="isSubscriptionOnly"
                    :is-subscription-button="isSubscriptionOnly && !isItemStatus"
                    :show-next-bill-info="true"
                    :clickable="!useIntentButtons"
                    @click="selectItemArea"
                    @select="selectItemButton(item, ...arguments)"
                    @viewSubscriptionOrders="selectSubscription(item)"
                  />
                  <div class="d-none campaign-product-id">{{item.campaign_product.campaign_product_id}}</div>
                  <hr />
                </div>
              </div>
            </div>
            <OrderTotalsInfo v-if="!isSubscriptionOnly" :session="session" :order="order" :key="'order-totals' + order.id.toString()" />
          </template>
        </div>
        <div v-else>
          <div class="centered-block">
            <div class="step-title">No items found</div>
          </div>
        </div>
      </b-col>
    </b-row>
    <b-row class="justify-content-center boxes select-item-btn mt-2 ml-sm-0 mr-sm-0 m-w-initial">
      <b-col v-if="showBackButton && !noback" md="4" lg="3" :cols="multiple ? '6': '12'" class="back p-0">
        <button type="button" @click="goBack()" class="btn-base new-btn-outline-success input-choice-btn stroke-2x">Back</button>
      </b-col>
      <b-col v-if="multiple" md="4" lg="3" cols="6" class="p-0">
        <button type="button" @click="done()" class="btn-base input-choice-btn font-weight-normal stroke-2x" :disabled="!canContinue">{{label}}
        </button>
      </b-col>
    </b-row>
  </section>
</template>

<script>
import axios from 'axios'
import {mapState} from 'vuex'
import {
  sortItems, getShortDate, getUTCShortDate, showSubscription, showFulfillment,
  getShortHumanReadableDate, getPaymentStatus, getPaymentStatusClass, getShortHumanReadableUTCDate,
  getItemIntents
} from '@/utils'
import {BillingCycleType, InputType, PaymentSourceStrings} from '@/constants'
import OrderTotalsInfo from '@/components/OrderTotalsInfo'
const OrderItemCard = () => import('./OrderItemCard')

export default {
  name: 'SelectItems',
  components: {OrderTotalsInfo, OrderItemCard},
  props: {
    session: Object,
    input: Object,
    label: String,
    defaults: {
      type: Array,
      default: function() {
        return []
      }
    },
    noback: {
      type: Boolean,
      default: function() {
        return false
      }
    },
    noselect: {
      type: Boolean,
      default: function() {
        return false
      }
    },
    multiple: {
      type: Boolean,
      default: function() {
        return false
      }
    },
    itemSelectLabel: {
      type: String,
      default: function() {
        return 'Select'
      }
    }
  },
  data: function() {
    return {
      getShortDate,
      getUTCShortDate,
      orders: [],
      selectedItemMap: {},
      canContinue: false,
      getShortHumanReadableDate,
      getShortHumanReadableUTCDate,
      initialLoading: true,
      selectedSubscriptionId: null,
      isSubscriptionOnly: false,
      isItemStatus: false
    }
  },
  computed: {
    ...mapState(['isLoading', 'campaign', 'debug', 'subscriptionOnly']),
    activeItemRequired: function() {
      return this.input && this.input.requires_active_item
    },
    showBackButton: function() {
      return this.session && (this.session.can_undo || this.session.is_step_preview)
    },
    isCheckout: function() {
      return this.input && (this.input.type === InputType.CheckoutConfirm)
    },
    useIntentButtons: function() {
      return !this.multiple && this.session && this.session.use_intents
    },
    additionalInputs: function() {
      return (this.session && this.session.additional_inputs) || []
    },
    allowedIntents: function() {
      let intents = []

      if (this.session) {
        if (this.useIntentButtons) {
          intents = this.session.intents
        } else {
          intents = this.additionalInputs.map((input) => input.type)
        }
      }

      return intents
    }
  },
  methods: {
    done: function() {
      if (this.canContinue) {
        let selectedItems = []

        this.orders.forEach(order => {
          order.items.forEach(item => {
            if (item.id in this.selectedItemMap) {
              selectedItems.push(item)
            }
          })
        })

        this.$emit('select', selectedItems) // done selecting items
      }
    },
    goBack: function() {
      if (this.selectedSubscriptionId) {
        this.selectedSubscriptionId = null
        this.isSubscriptionOnly = true
        this.init() // refresh
      } else {
        this.$emit('back') // go back to the previous step (ignore changes to item selection)
      }
    },
    selectSubscription: function(item) {
      if (item.subscription_id && !this.isItemStatus) {
        this.selectedSubscriptionId = item.subscription_id
        this.isSubscriptionOnly = false
        this.init() // refresh
      }
    },
    showFulfillment: function(item) {
      return !this.isSubscriptionOnly && showFulfillment(item)
    },
    showSubscription: function(item) {
      return showSubscription(item)
    },
    getPaymentStatus: function(order) {
      return getPaymentStatus(order, false)
    },
    getPaymentStatusClass: function(order) {
      return getPaymentStatusClass(order, false)
    },
    selectItemArea: function(event, item) {
      event.stopPropagation()

      if (!this.useIntentButtons) {
        this.selectItemButton(item)
      }
    },
    getGatewayString: function(order) {
      let gateway = PaymentSourceStrings[order.payment_type]

      if (order.gateway_id) {
        gateway += ' gateway ' + order.gateway_id
      } else {
        gateway += ' payment method'
      }

      return gateway
    },
    selectItemButton: function(item, intent) {
      if (this.multiple) {
        if (item.id in this.selectedItemMap) {
          delete this.selectedItemMap[item.id]
        } else {
          this.selectedItemMap[item.id] = true
        }

        this.updateStatus()
      } else if (this.useIntentButtons) {
        this.$emit('select', [item], intent) // select intent button
      } else if (intent) {
        const input = this.additionalInputs.find((i) => i.type === intent)

        if (input) {
          this.$emit('selectItemInput', {item, input}) // select legacy additional intent input
        }
      } else {
        this.$emit('select', [item]) // select legacy item button (i.e. get help)
      }
    },
    isSelected: function(item) {
      return this.selectedItemMap && !!(item.id in this.selectedItemMap)
    },
    selectAll: function() {
      this.orders.forEach(order => {
        this.selectOrder(order)
      })

      this.updateStatus()
    },
    clearAll: function() {
      this.selectedItemMap = {}
      this.updateStatus()
    },
    selectOrder: function(order) {
      order.items.forEach(item => {
        this.selectedItemMap[item.id] = true
      })

      this.updateStatus()
    },
    clearOrder: function(order) {
      order.items.forEach(item => {
        if (item.id in this.selectedItemMap) {
          delete this.selectedItemMap[item.id]
        }
      })

      this.updateStatus()
    },
    updateStatus: function() {
      // update the select all checkbox
      let selectionCount = Object.keys(this.selectedItemMap).length
      this.canContinue = selectionCount !== 0

      this.orders = this.orders.slice() // force item statuses to be called
    },
    init: function() {
      this.$store.commit('busy')
      this.selectedItemMap = {}
      const sessionIntents = new Set(this.allowedIntents)

      // initialize the set of default selected items
      if (this.multiple) {
        this.defaults.forEach(item => {
          this.selectedItemMap[item.id] = true
        })
      }

      let params = {
        is_active: this.activeItemRequired,
        checkout: this.isCheckout
      }

      if (this.isSubscriptionOnly) {
        params = {
          latest_cycle: true,
          billing_cycle_type: BillingCycleType.Recurring
        }

        if (
          this.isItemStatus &&
          this.session &&
          this.session.selected_item &&
          this.session.selected_item.subscription_id
        ) {
          params['subscription_id'] = this.session.selected_item.subscription_id
        }
      } else if (this.selectedSubscriptionId) {
        params = {subscription_id: this.selectedSubscriptionId}
      }

      axios.get('/order_items/', {params: params}).then(result => {
        let items = result.data.results
        let lastOrderId = null
        let orders = []
        let order = {}
        let startIndex = 0
        const settings = this.campaign.portal_settings || {}

        sortItems(items)

        for (let i = 0; i < items.length; i++) {
          let orderId = items[i].order.id
          items[i]['actions'] = getItemIntents(items[i], settings).filter(intent => sessionIntents.has(intent))

          if (orderId !== lastOrderId) { // if this is the 1st item of a different order
            if (i > 0) {
              // if this is not the 1st item, then copy the items from the previous order
              order['items'] = items.slice(startIndex, i)
              orders.push(order)
            }

            lastOrderId = orderId
            startIndex = i
            order = Object.assign({}, items[i].order)
          }

          if (i === (items.length - 1)) { // if this is the last item
            order['items'] = items.slice(startIndex)
            orders.push(order)
          }
        }

        this.orders = orders
        this.updateStatus()
        this.$store.commit('ready')

        if (!items.length) {
          this.canContinue = true
        }
        this.initialLoading = false
      }, error => {
        console.error(error)
        this.$store.commit('critical_error', error)
        this.$store.commit('ready')
        this.initialLoading = false
      })
    }
  },
  created() {
    if (this.input) {
      this.isSubscriptionOnly = (this.input.type === InputType.SubscriptionItem)
      this.$store.commit('set_subscription_only', this.isSubscriptionOnly)
    } else {
      this.isSubscriptionOnly = this.subscriptionOnly
      this.isItemStatus = true
    }

    this.init()
  }
}
</script>

<style scoped>
</style>
